import { useEffect } from 'react';
import type { PageJson } from '../../CardCustomizer.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetEventPrivacyForCardCustomizerQuery } from '../../CardCustomizer.graphql.generated';
import { TEMPLATE_PLACEHOLDER_LINK } from '../../CardCustomizer.constants';
import { addGuestSiteUtmParams, isJoyHost, isJoyWebsiteUrl } from '@shared/utils/guestSiteUrl';

/**
 * Inputs needed to populate stationery event-link layers. Extracted from the hook so the
 * logic below can be unit-tested without the React/GraphQL machinery.
 */
export type PopulateEventLinkContext = Readonly<{
  eventHandle: string;
  env: string; // 'dev.' for local/dev, '' for prod
  shouldShowPassword: boolean;
  eventPassword: string;
}>;

/**
 * Try to parse a URL, accepting both protocol-prefixed (`https://withjoy.com/...`) and
 * protocol-less (`withjoy.com/...`) values. Stationery draft content may omit the protocol
 * (the template placeholder is `withjoy.com/name-and-name`), and some legacy drafts store
 * URLs without a scheme — both need to be recognised.
 */
const tryParseUrl = (candidate: string | undefined): URL | null => {
  if (!candidate) return null;
  try {
    return new URL(candidate);
  } catch {
    try {
      return new URL(`https://${candidate}`);
    } catch {
      return null;
    }
  }
};

/**
 * Populates / normalises event-link layers on a stationery draft in place.
 *
 * Text layer (`layer.name === 'URL'`):
 *   - Empty → fill with the bare current-event URL
 *   - Stale joy URL (different handle) → overwrite with current-event URL
 *   - Correct-handle joy URL (with or without UTMs, any subpath like `/{handle}/rsvp`) → preserve
 *   - Custom third-party URL → preserve
 *
 * QR layer:
 *   - Placeholder → fill with UTM-tagged current-event URL (scan attributes `stationery`)
 *   - Stale joy URL → overwrite with UTM-tagged current-event URL
 *   - Correct-handle joy URL (any subpath) → preserve
 *   - Custom third-party URL → preserve
 *   - Password: attached to any joy URL, stripped for lookalike hosts. Re-read after any
 *     overwrite so the decision reflects the effective link, not a stale pre-overwrite value.
 *
 * Exported so `usePopulateEventLink.spec.ts` can cover the heuristic directly.
 */
export const populateEventLinkInDraft = (draft: PageJson, ctx: PopulateEventLinkContext): void => {
  const { eventHandle, env, shouldShowPassword, eventPassword } = ctx;

  // Text layer uses the bare URL so the printed card shows a clean, readable link.
  // QR layer uses the UTM-tagged URL so scans attribute to the `stationery` campaign. QRCodeLayer
  // strips UTMs from the human-readable label below the QR, so the printed URL stays clean there too.
  const textLayerLink = `https://${env}withjoy.com/${eventHandle}`;
  const qrLayerLink = addGuestSiteUtmParams(textLayerLink, {
    utm_medium: 'web',
    utm_source: 'joy',
    utm_campaign: 'stationery',
  });

  /** True if `candidate` points at this event (matches `/{handle}` or any `/{handle}/...` subpath). */
  const isUrlForThisEvent = (candidate: string | undefined): boolean => {
    const parsed = tryParseUrl(candidate);
    if (!parsed || !isJoyHost(parsed.hostname)) return false;
    return parsed.pathname === `/${eventHandle}` || parsed.pathname.startsWith(`/${eventHandle}/`);
  };

  /** True if `candidate` is a joy URL but not for this event (stale after handle rename). */
  const isStaleJoyUrl = (candidate: string | undefined): boolean => {
    const parsed = tryParseUrl(candidate);
    if (!parsed) return false;
    return isJoyHost(parsed.hostname) && !isUrlForThisEvent(candidate);
  };

  for (const layer of draft.layers) {
    if (layer.type === 'text' && layer.name === 'URL') {
      const current = layer.textData.content;
      if (!current || isStaleJoyUrl(current)) {
        layer.textData.content = textLayerLink;
      }
      // else: correct-handle joy URL or custom third-party URL — preserve.
    }
    if (layer.type === 'qrcode') {
      const currentBefore = layer.qrcodeData.link;
      const hasPlaceholder = !currentBefore || currentBefore === TEMPLATE_PLACEHOLDER_LINK;
      if (hasPlaceholder || isStaleJoyUrl(currentBefore)) {
        layer.qrcodeData.link = qrLayerLink;
      }
      // Re-read after the conditional overwrite — the password decision must reflect the
      // effective link (post-overwrite), not the pre-overwrite placeholder / stale value.
      // Password attaches to any joy URL; only lookalike hosts lose it (strictly safer).
      const effectiveLink = layer.qrcodeData.link;
      const isJoyEventLink = !!effectiveLink && isJoyWebsiteUrl(effectiveLink);
      layer.qrcodeData.password = isJoyEventLink && shouldShowPassword ? eventPassword : undefined;
    }
  }
};

export const usePopulateEventLink = (onPrivacyDataChange: (cb: (draft: PageJson) => void) => void) => {
  const { eventInfo, eventHandle } = useEventInfo();

  const { data: privacyData } = useGetEventPrivacyForCardCustomizerQuery({
    variables: {
      eventId: eventInfo?.eventId ?? '',
    },
    batchMode: 'fast',
    skip: !eventInfo?.eventId,
  });

  const populateEventLink = (draft: PageJson) => {
    if (!privacyData) return;
    populateEventLinkInDraft(draft, {
      eventHandle: eventHandle ?? '',
      env: process.env.NODE_ENV === 'development' ? 'dev.' : '',
      shouldShowPassword: privacyData.eventById?.settings.privacyMode !== 'public',
      eventPassword: privacyData.eventById?.info.eventPassword || '',
    });
  };

  useEffect(() => {
    onPrivacyDataChange(populateEventLink);
    // Disabling-next-line because `update` is not stable (larger issue),
    // however the stale `update` still works.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacyData]);

  return { populateEventLink };
};
